import { httpClient } from "../http-client";
import { NEW_API_ENDPOINTS } from "../../../shared/constants/api";
import type {
  ApiResponseProductsEntityList,
  ApiResponseShopBannerArrayEntity,
  ApiResponseShopBannerEntity,
  ApiResponseShopFooterDesign,
  ApiResponseShopHeaderDesign,
  ApiResponseShopManagementDto,
  ApiResponseShopSectionEntity,
  ApiResponseShopSectionEntityList,
  ApiResponseVoid,
  CreateBannerRequest,
  CreateShopSectionCommand,
  DeleteBannerRequest,
  UpdateBannerRequest,
  UpdateBestSellerCommand,
  UpdateShopFooterDesignCommand,
  UpdateShopHeaderDesignCommand,
  UpdateShopSectionCommand,
  UpdateShopSectionOrderCommand,
  UpdateShopSettingsCommand,
} from "../../../shared/types/api";

export class CompanyShopApiService {
  async getShopHome(companyId: number): Promise<ApiResponseShopManagementDto> {
    const response = await httpClient.get<ApiResponseShopManagementDto>(
      NEW_API_ENDPOINTS.companyShopController.GET.shopHome(companyId),
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "쇼핑몰 정보를 불러오지 못했습니다.",
      );
    }

    return response.data;
  }

  async updateShopSettings(
    companyId: number,
    request: UpdateShopSettingsCommand,
  ): Promise<ApiResponseShopManagementDto> {
    const response = await httpClient.patch<ApiResponseShopManagementDto>(
      NEW_API_ENDPOINTS.companyShopController.PATCH.setting(companyId),
      request,
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "쇼핑몰 정보를 업데이트하지 못했습니다.",
      );
    }

    return response.data;
  }

  async updateBestSellers(
    companyId: number,
    request: UpdateBestSellerCommand,
  ): Promise<ApiResponseShopManagementDto> {
    const response = await httpClient.put<ApiResponseShopManagementDto>(
      NEW_API_ENDPOINTS.companyShopController.PUT.bestSellers(companyId),
      request,
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "베스트셀러를 업데이트하지 못했습니다.",
      );
    }

    return response.data;
  }

  async getShopBanners(
    companyId: number,
  ): Promise<ApiResponseShopBannerArrayEntity> {
    const response = await httpClient.get<ApiResponseShopBannerArrayEntity>(
      NEW_API_ENDPOINTS.companyShopController.GET.banner(companyId),
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "쇼핑몰 설정을 업데이트하지 못했습니다.",
      );
    }

    return response.data;
  }

  async createShopBanners(
    request: CreateBannerRequest,
  ): Promise<ApiResponseShopBannerEntity> {
    const formData = new FormData();
    formData.append("companyId", request.companyId.toString());
    formData.append(
      "command",
      new Blob([JSON.stringify(request.command)], { type: "application/json" }),
    );
    formData.append("bannerFile", request.bannerFile);

    const response = await httpClient.post<ApiResponseShopBannerEntity>(
      NEW_API_ENDPOINTS.companyShopController.POST.banner(request.companyId),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "배너를 생성하지 못했습니다.");
    }

    return response.data;
  }

  async updateShopBanners(
    request: UpdateBannerRequest,
  ): Promise<ApiResponseShopBannerEntity> {
    const formData = new FormData();
    formData.append("command", new Blob([JSON.stringify(request.command)], { type: "application/json" }));
    if (request.bannerFile) {
      formData.append("bannerFile", request.bannerFile);
    }

    const response = await httpClient.put<ApiResponseShopBannerEntity>(
      NEW_API_ENDPOINTS.companyShopController.PUT.banner(
        request.companyId,
        request.bannerId,
      ),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "배너를 생성하지 못했습니다.");
    }

    return response.data;
  }

  async deleteBanner(request: DeleteBannerRequest): Promise<ApiResponseVoid> {
    const response = await httpClient.delete<ApiResponseVoid>(
      NEW_API_ENDPOINTS.companyShopController.DELETE.banner(
        request.companyId,
        request.bannerId,
      ),
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "쇼핑몰 설정을 업데이트하지 못했습니다.",
      );
    }

    return response.data;
  }

  async getShopProducts(
    companyId: number,
  ): Promise<ApiResponseProductsEntityList> {
    const response = await httpClient.get<ApiResponseProductsEntityList>(
      NEW_API_ENDPOINTS.companyShopController.GET.shopProducts(companyId),
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "쇼핑몰 설정을 업데이트하지 못했습니다.",
      );
    }

    return response.data;
  }

  async updateStripBanner(params: {
    companyId: number;
    bannerFile?: File;
    command: { enabled: boolean; link?: string | null };
  }): Promise<ApiResponseShopManagementDto> {
    const formData = new FormData();
    formData.append(
      "command",
      new Blob([JSON.stringify(params.command)], { type: "application/json" }),
    );
    if (params.bannerFile) {
      formData.append("bannerFile", params.bannerFile);
    }

    const response = await httpClient.put<ApiResponseShopManagementDto>(
      NEW_API_ENDPOINTS.companyShopController.PUT.stripBanner(params.companyId),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "띠배너를 업데이트하지 못했습니다.",
      );
    }

    return response.data;
  }

  async deleteStripBanner(
    companyId: number,
  ): Promise<ApiResponseShopManagementDto> {
    const response = await httpClient.delete<ApiResponseShopManagementDto>(
      NEW_API_ENDPOINTS.companyShopController.DELETE.stripBanner(companyId),
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "띠배너를 삭제하지 못했습니다.",
      );
    }

    return response.data;
  }

  /* ---------------------------------------------------------------- *
   * 메인 섹션 / 헤더 / 푸터
   * 기존 PATCH /shop/setting 은 전체 치환이라 여기에 섞지 않고 전용 엔드포인트를 쓴다.
   * ---------------------------------------------------------------- */

  async getShopSections(
    companyId: number,
  ): Promise<ApiResponseShopSectionEntityList> {
    const response = await httpClient.get<ApiResponseShopSectionEntityList>(
      NEW_API_ENDPOINTS.companyShopController.GET.section(companyId),
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "섹션 목록을 불러오지 못했습니다.",
      );
    }

    return response.data;
  }

  async createShopSection(
    companyId: number,
    request: CreateShopSectionCommand,
  ): Promise<ApiResponseShopSectionEntity> {
    const response = await httpClient.post<ApiResponseShopSectionEntity>(
      NEW_API_ENDPOINTS.companyShopController.POST.section(companyId),
      request,
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "섹션을 생성하지 못했습니다.");
    }

    return response.data;
  }

  async updateShopSection(
    companyId: number,
    sectionId: number,
    request: UpdateShopSectionCommand,
  ): Promise<ApiResponseShopSectionEntity> {
    const response = await httpClient.put<ApiResponseShopSectionEntity>(
      NEW_API_ENDPOINTS.companyShopController.PUT.section(companyId, sectionId),
      request,
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "섹션을 수정하지 못했습니다.");
    }

    return response.data;
  }

  async deleteShopSection(
    companyId: number,
    sectionId: number,
  ): Promise<ApiResponseVoid> {
    const response = await httpClient.delete<ApiResponseVoid>(
      NEW_API_ENDPOINTS.companyShopController.DELETE.section(
        companyId,
        sectionId,
      ),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "섹션을 삭제하지 못했습니다.");
    }

    return response.data;
  }

  async updateShopSectionOrder(
    companyId: number,
    request: UpdateShopSectionOrderCommand,
  ): Promise<ApiResponseShopSectionEntityList> {
    const response = await httpClient.put<ApiResponseShopSectionEntityList>(
      NEW_API_ENDPOINTS.companyShopController.PUT.sectionOrder(companyId),
      request,
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "섹션 순서를 저장하지 못했습니다.",
      );
    }

    return response.data;
  }

  async updateShopHeaderDesign(
    companyId: number,
    request: UpdateShopHeaderDesignCommand,
  ): Promise<ApiResponseShopHeaderDesign> {
    const response = await httpClient.patch<ApiResponseShopHeaderDesign>(
      NEW_API_ENDPOINTS.companyShopController.PATCH.header(companyId),
      request,
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "헤더 디자인을 저장하지 못했습니다.",
      );
    }

    return response.data;
  }

  async updateShopFooterDesign(
    companyId: number,
    request: UpdateShopFooterDesignCommand,
  ): Promise<ApiResponseShopFooterDesign> {
    const response = await httpClient.patch<ApiResponseShopFooterDesign>(
      NEW_API_ENDPOINTS.companyShopController.PATCH.footer(companyId),
      request,
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "푸터 디자인을 저장하지 못했습니다.",
      );
    }

    return response.data;
  }
}

export const companyShopApiService = new CompanyShopApiService();
